import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import * as Icons from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AdminDashboard from "@/components/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT = {
  phoneDisplay: "+91 99955 88418",
  phoneLink: "tel:+919995588418",
  email: "care@healbayconnect.com",
  emailLink: "mailto:care@healbayconnect.com",
  whatsappLink:
    "https://wa.me/919995588418?text=Hello%20HealBay%20Connect%2C%20I%20would%20like%20to%20discuss%20medical%20treatment%20options%20in%20India.",
};

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/healbayconnect/",
  facebook: "https://www.facebook.com/profile.php?id=61573993378396",
};

const GOOGLE_REVIEWS_URL = "https://share.google/jPwG8nquklCslihaf";
const GOOGLE_WRITE_REVIEW_URL = "https://g.page/r/CZDzvV3kf9fgEAI/review";
const GOOGLE_RATING = { value: "5.0", count: 5 };

const GOOGLE_REVIEWS = [
  {
    name: "Ameen Faisal",
    initials: "AF",
    rating: 5,
    quote:
      "Excellent service from HealBay! They provide professional and reliable assistance with medical appointments, hospital arrangements, travel planning, and other medical tourism-related services. Especially Yunus Muhammad, who is always helpful, supportive, and dedicated. Keep up the good work, Yunus! Wishing you and HealBay continued success.",
    dir: "ltr",
  },
  {
    name: "Yousuf Alhusaini",
    initials: "YA",
    rating: 5,
    quote: "ممتاز جدا",
    dir: "rtl",
  },
  {
    name: "Fathimath Sameer",
    initials: "FS",
    rating: 5,
    quote:
      "It’s wonderful to see you continuing your dedication to patient care, Muhammed! For anyone in the Maldives seeking medical treatment in India, I highly recommend reaching out to him. His services are well-tested and trusted by Maldivians, making the process much smoother and more affordable.",
    dir: "ltr",
  },
];

const GoogleGIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.2 5.5-4.7 7.2l7.6 5.9c4.4-4.1 6.9-10.1 6.9-17.6z"
    />
    <path
      fill="#FBBC05"
      d="M10.5 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.1z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.6-5.9c-2.1 1.4-4.8 2.3-8 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"
    />
  </svg>
);

const LOGO_NAVBAR_URL = "/logo-new-cropped.png";
const LOGO_FOOTER_URL = "/logo-new-cropped.png";

const ICON_FALLBACK = Icons.Stethoscope;

const getTrackStride = (track) =>
  track.children.length > 1
    ? Math.abs(track.children[1].offsetLeft - track.children[0].offsetLeft)
    : track.clientWidth;

const scrollTrackTo = (track, index) => {
  const direction = document.documentElement.dir === "rtl" ? -1 : 1;
  track.scrollTo({
    left: direction * index * getTrackStride(track),
    behavior: "smooth",
  });
};

const ReviewCarousel = ({ t }) => {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const handleScroll = () => {
      const index = Math.round(Math.abs(track.scrollLeft) / getTrackStride(track));
      setActiveIndex(Math.min(Math.max(index, 0), GOOGLE_REVIEWS.length - 1));
    };
    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mobileQuery.matches || reduceMotion.matches) return undefined;
    const timer = window.setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      scrollTrackTo(track, (activeIndex + 1) % GOOGLE_REVIEWS.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [activeIndex]);

  const goTo = (index) => {
    const track = trackRef.current;
    if (track) scrollTrackTo(track, index);
  };

  return (
    <div className="space-y-4" data-testid="testimonials-carousel">
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-3 md:overflow-visible md:pb-0"
        data-testid="testimonials-grid"
      >
        {GOOGLE_REVIEWS.map((review, index) => (
          <Card
            key={review.name}
            className="w-full shrink-0 snap-center border border-slate-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg md:w-auto"
            data-testid={`testimonial-card-${index + 1}`}
          >
            <CardContent className="flex h-full flex-col gap-4 p-6">
              <div
                className="flex items-center justify-between gap-3"
                data-testid={`testimonial-author-row-${index + 1}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cyan-600 text-sm font-bold text-white"
                    data-testid={`testimonial-avatar-${index + 1}`}
                  >
                    {review.initials}
                  </span>
                  <div>
                    <p
                      className="text-sm font-semibold text-slate-900"
                      data-testid={`testimonial-name-${index + 1}`}
                    >
                      {review.name}
                    </p>
                    <p
                      className="text-xs text-slate-500"
                      data-testid={`testimonial-verified-${index + 1}`}
                    >
                      {t.testimonials.verifiedLabel}
                    </p>
                  </div>
                </div>
                <GoogleGIcon className="h-5 w-5 shrink-0" />
              </div>
              <div
                className="flex items-center gap-1"
                data-testid={`testimonial-stars-${index + 1}`}
              >
                {Array.from({ length: review.rating }).map((_, starIndex) => (
                  <Icons.Star
                    key={`${review.name}-star-${starIndex}`}
                    className="h-4 w-4 fill-amber-400 text-amber-400"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p
                dir={review.dir}
                className={`flex-1 text-sm leading-relaxed text-slate-700 ${review.dir === "rtl" ? "text-right" : "text-left"}`}
                data-testid={`testimonial-text-${index + 1}`}
              >
                {review.quote}
              </p>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                title={t.testimonials.badgeTitle}
                className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors duration-300 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800"
                data-testid={`testimonial-google-badge-${index + 1}`}
              >
                <GoogleGIcon className="h-4 w-4" />
                <span>{t.testimonials.badgeLabel}</span>
                <Icons.ExternalLink
                  className="h-3 w-3 text-slate-400"
                  aria-hidden="true"
                />
              </a>
            </CardContent>
          </Card>
        ))}
      </div>
      <div
        className="flex items-center justify-center gap-2 md:hidden"
        data-testid="testimonials-dots"
      >
        {GOOGLE_REVIEWS.map((review, index) => (
          <button
            key={`dot-${review.name}`}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`${t.testimonials.verifiedLabel} ${index + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === activeIndex ? "w-6 bg-cyan-600" : "w-2 bg-slate-300"
            }`}
            data-testid={`testimonials-dot-${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const StomachIcon = ({ className, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M9 3.5v4.2c0 1.5.8 2.8 2.1 3.4l1.8.9c2.2 1.1 3.6 3.3 3.6 5.7v.3a3.9 3.9 0 0 1-3.9 3.9h-.8A6.7 6.7 0 0 1 5 15.2v-1.4A6.8 6.8 0 0 1 9 7.6" />
    <path d="M9 3.5h3.6" />
    <path d="M15.8 12.7c-1.1.2-1.9 1.1-1.9 2.3" />
  </svg>
);

const UrologyIcon = ({ className, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M8.5 6.5a2.5 2.5 0 0 0-2.5 2.5v1.4a4.1 4.1 0 0 0 2.8 3.9l1.5.5" />
    <path d="M15.5 6.5A2.5 2.5 0 0 1 18 9v1.4a4.1 4.1 0 0 1-2.8 3.9l-1.5.5" />
    <path d="M12 11.5v8.5" />
    <path d="M10.3 19.2h3.4" />
  </svg>
);

const GynecologyIcon = ({ className, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M7 9.5a2.8 2.8 0 1 0 0 5.6" />
    <path d="M17 9.5a2.8 2.8 0 1 1 0 5.6" />
    <path d="M7 12.3h10" />
    <path d="M9.4 12.3a2.6 2.6 0 0 0 5.2 0" />
    <path d="M12 14.9v4.3" />
    <path d="M10.6 19.2h2.8" />
  </svg>
);

const WhatsAppBrandIcon = ({ className, ...props }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={className} {...props}>
    <path
      fillRule="evenodd"
      d="M13.601 2.326A7.85 7.85 0 0 0 8 0C3.582 0 0 3.582 0 8c0 1.406.367 2.778 1.064 3.989L0 16l4.152-1.088A7.945 7.945 0 0 0 8 16h.003c4.418 0 8-3.582 8-8a7.948 7.948 0 0 0-2.402-5.674zM8.003 14.5a6.48 6.48 0 0 1-3.301-.902l-.236-.141-2.463.646.657-2.403-.154-.247a6.476 6.476 0 0 1-.996-3.453c0-3.584 2.916-6.5 6.5-6.5 1.736 0 3.365.676 4.593 1.906A6.455 6.455 0 0 1 14.5 8c0 3.584-2.916 6.5-6.497 6.5zm3.79-4.84c-.208-.104-1.231-.607-1.422-.676-.19-.07-.329-.104-.468.104-.139.208-.537.676-.659.815-.121.139-.243.156-.451.052-.208-.104-.878-.323-1.673-1.03-.618-.55-1.036-1.23-1.157-1.438-.121-.208-.013-.32.091-.424.094-.093.208-.243.312-.364.104-.121.139-.208.208-.347.069-.139.035-.26-.017-.364-.052-.104-.468-1.13-.642-1.55-.169-.406-.341-.351-.468-.357l-.399-.007a.768.768 0 0 0-.555.26c-.19.208-.728.711-.728 1.734 0 1.023.746 2.011.85 2.15.104.139 1.47 2.243 3.564 3.146.499.215.888.343 1.191.439.5.159.955.137 1.314.083.401-.06 1.231-.503 1.405-.989.173-.486.173-.902.121-.989-.052-.087-.191-.139-.399-.243z"
      clipRule="evenodd"
    />
  </svg>
);

const getIcon = (iconName) => {
  if (iconName === "Stomach") {
    return StomachIcon;
  }
  if (iconName === "UrologySpecialty") {
    return UrologyIcon;
  }
  if (iconName === "GynecologySpecialty") {
    return GynecologyIcon;
  }
  return Icons[iconName] || ICON_FALLBACK;
};

const CONTENT = {
  en: {
    metaTitle: "HealBay Connect | Medical Treatment Coordination in India",
    metaDescription:
      "HealBay Connect supports international patients with trusted hospitals in Kerala, transparent medical coordination, and personalized treatment journey support.",
    metaKeywords:
      "medical treatment in India, medical tourism India, hospitals in Kerala for international patients, affordable surgery India, medical coordination India",
    brand: "HealBay Connect",
    nav: [
      { label: "About", href: "#about" },
      { label: "Hospitals", href: "#hospitals" },
      { label: "Specialties", href: "#specialties" },
      { label: "Services", href: "#services" },
      { label: "Process", href: "#process" },
      { label: "Contact", href: "#inquiry" },
    ],
    hero: {
      eyebrow: "Medical Tourism India",
      journeyHighlight:
        "Clinical Excellence • Accredited Hospitals • Recovery Support",
      journeySubHighlight:
        "Oncology, Cardiology, Neurosurgery & more with trusted international coordination",
      title: "Trusted Medical Treatment Coordination in India",
      subtitle:
        "Access world-class clinical care and personalized recovery pathways across Kerala's top JCI and NABH accredited hospitals. Over a decade of combined expertise in international patient coordination.",
      description:
        "HealBay Connect supports international families seeking medical treatment in India, medical tourism in India, affordable surgery in India, and trusted hospital coordination in Kerala through transparent, specialist-led care pathways.",
      ctaWhatsapp: "Chat on WhatsApp",
      ctaCall: "Call Now",
      ctaEmail: "Send Email Inquiry",
      badge: "Nearly 10 years of combined coordination experience",
      logoIntroTitle: "Trusted Care Starts Here",
      logoIntroSubtitle:
        "Scroll to explore treatment coordination, hospital partners, and recovery support details.",
      logoIntroButton: "View Details",
    },
    about: {
      title: "About HealBay Connect",
      body: "HealBay Connect is a medical treatment coordination service based in Kerala, India, built to simplify healthcare access for international patients. We focus on trust, transparency, and personalized guidance from first contact to post-treatment communication.",
      highlights: [
        "Nearly 10 years of combined experience in medical coordination",
        "Support for international patients navigating treatment in India",
        "Transparent communication and ethical coordination",
        "English and Arabic patient support",
        "Personalized guidance throughout the treatment journey",
      ],
    },
    hospitals: {
      title: "Leading Hospital Partners",
      intro:
        "Our partner hospitals are leading multi-specialty institutions in Kerala with advanced medical care and international patient services.",
      cards: [
        {
          name: "Aster Medcity Kochi",
          accreditation: ["JCI Accredited", "NABH Accredited"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/m8ngnnhg_69387.jpg",
          description:
            "Comprehensive tertiary care with internationally recognized departments and patient services.",
        },
        {
          name: "Rajagiri Hospital",
          accreditation: ["NABH Accredited", "ISO Certified"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/kdr75giw_69388.jpg",
          description:
            "Advanced diagnostics and specialist-led treatment pathways focused on quality outcomes.",
        },
        {
          name: "VPS Lakeshore Hospital",
          accreditation: ["NABH Accredited", "ISO Certified"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/tav6yx8v_69389.jpg",
          description:
            "Well-established multi-specialty expertise with strong support for international patients.",
        },
        {
          name: "Adlux Apollo Hospital",
          accreditation: ["NABH Aligned", "Apollo Clinical Protocols"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/1j3qctgi_unnamed.jpg",
          description:
            "Modern multi-specialty care with Apollo-standard clinical protocols, advanced diagnostics, and reliable support for international patients.",
        },
      ],
    },
    specialties: {
      title: "Medical Specialties",
      items: [
        {
          title: "Orthopaedics",
          description:
            "Bone, joint, and mobility treatments from trusted specialists.",
          icon: "Bone",
        },
        {
          title: "Neurology & Neurosurgery",
          description:
            "Expert care for brain, spine, and nervous system conditions.",
          icon: "Brain",
        },
        {
          title: "Urology",
          description:
            "Personalized urological evaluation and treatment support.",
          icon: "UrologySpecialty",
        },
        {
          title: "Cardiology",
          description:
            "Comprehensive heart care with advanced diagnostics and procedures.",
          icon: "HeartPulse",
        },
        {
          title: "Gastroenterology",
          description:
            "Digestive health care coordinated with leading GI specialists.",
          icon: "Stomach",
        },
        {
          title: "Gynecology",
          description:
            "Women-focused care pathways with compassionate specialist support.",
          icon: "GynecologySpecialty",
        },
        {
          title: "Oncology",
          description:
            "Structured guidance for cancer consultation, treatment, and follow-up.",
          icon: "ShieldPlus",
        },
        {
          title: "Pediatrics",
          description:
            "Child-focused specialist care pathways with family-centered coordination support.",
          icon: "Baby",
        },
      ],
    },
    services: {
      title: "Services Offered",
      items: [
        {
          title: "Medical report evaluation",
          icon: "FileSearch",
        },
        {
          title: "Treatment plan coordination with hospitals",
          icon: "ClipboardCheck",
        },
        {
          title: "Transparent cost estimation",
          icon: "BadgeIndianRupee",
        },
        {
          title: "Appointment scheduling with specialists",
          icon: "CalendarCheck",
        },
        {
          title: "Visa assistance",
          icon: "Globe",
        },
        {
          title: "Travel and accommodation guidance",
          icon: "Plane",
        },
        {
          title: "Dedicated patient coordination",
          icon: "Users",
        },
        {
          title: "Post-treatment follow-up communication",
          icon: "MessageSquareText",
        },
      ],
    },
    conciergeBanner: {
      headline: "Clinical Care First, Recovery Enhanced by Kerala",
      subheadline:
        "Receive specialist-led treatment pathways, family-friendly recovery planning, and coordinated wellness support through one trusted medical coordination partner.",
      cta: "Speak With Our Travel Concierge",
      image:
        "https://images.unsplash.com/photo-1629813538702-64c925934e19?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    process: {
      title: "How the Process Works",
      steps: [
        "Contact HealBay Connect and share medical reports",
        "Receive customized treatment pathways and transparent cost estimates from partner hospitals within 48 hours",
        "Plan travel to Kerala with scheduled appointments",
        "Receive treatment and recovery support during your stay",
      ],
    },
    why: {
      title: "Why Choose HealBay Connect",
      points: [
        "Nearly 10 years of experience in healthcare coordination",
        "Trusted hospital network across Kerala",
        "Transparent and ethical communication",
        "Dedicated support throughout your medical journey",
        "Arabic and English assistance for international patients",
        "Strong focus on patient comfort, clarity, and confidence",
      ],
    },
    testimonials: {
      title: "Google Reviews from International Patients",
      subtitle:
        "Trusted experiences shared by families supported through HealBay Connect treatment coordination.",
      badgeLabel: "View on Google",
      badgeTitle: "Open HealBay Connect reviews on Google",
      ratingSuffix: "Based on {count} Google reviews",
      verifiedLabel: "Verified Google review",
      shareTitle: "Have you been treated with us?",
      shareButton: "Share your experience",
    },
    inquiry: {
      title: "Email Inquiry Form",
      subtitle:
        "Share your medical details securely. Our team supports both English and Arabic communication, including documentation guidance for GCC families.",
      fields: {
        fullName: "Full Name",
        country: "Country",
        phone: "Phone / WhatsApp",
        email: "Email",
        treatmentType: "Treatment Type",
        message: "Medical Summary / Inquiry Message",
      },
      uploadLabel: "Upload Medical Reports",
      uploadHelp:
        "Accepted formats: PDF/JPG/PNG. Include latest reports for faster clinical review.",
      securityNote:
        "🛡️ Your medical records are encrypted, kept strictly confidential, and shared exclusively with our accredited hospital clinical boards for treatment evaluation.",
      submit: "Submit Inquiry",
      success: "Thank you. Your inquiry has been received.",
      error:
        "Unable to submit right now. Please try WhatsApp or call us directly.",
      validationError: "Please check your form details and try again.",
    },
    cta: {
      title: "Start Your Treatment Journey in India with HealBay Connect",
      description:
        "Connect with our team for transparent treatment coordination and compassionate support.",
      whatsapp: "WhatsApp Consultation",
      call: "Call Our Team",
      email: "Email Inquiry",
    },
    footer: {
      location: "Kerala, India",
      disclaimer:
        "HealBay Connect provides medical coordination and facilitation services. Medical treatment is provided by licensed hospitals and healthcare professionals.",
      privacy: "Privacy Policy",
      adminAccess: "Admin Dashboard Login",
      socialTitle: "Connect With Us",
      instagram: "Instagram",
      facebook: "Facebook",
      rights: "© HealBay Connect. All rights reserved.",
    },
    privacyPolicy: {
      title: "Privacy Policy",
      intro:
        "HealBay Connect is committed to protecting all personal and medical information shared through this website.",
      points: [
        "We collect only essential information required for treatment coordination, including contact details and medical summaries.",
        "All submitted medical records are encrypted and handled under strict confidentiality protocols.",
        "Patient data is shared only with accredited partner hospital clinical boards for evaluation and treatment planning.",
        "We do not sell or disclose patient information to unauthorized third parties.",
      ],
      contact: "For privacy requests, contact: care@healbayconnect.com",
    },
    floating: "WhatsApp",
    languageButton: "العربية",
  },
  ar: {
    metaTitle: "هيل باي كونيكت | تنسيق العلاج الطبي في الهند",
    metaDescription:
      "هيل باي كونيكت تدعم المرضى الدوليين للوصول إلى مستشفيات موثوقة في كيرالا مع تنسيق طبي شفاف ودعم شخصي.",
    metaKeywords:
      "العلاج الطبي في الهند, السياحة العلاجية في الهند, مستشفيات كيرالا للمرضى الدوليين, جراحات ميسورة التكلفة في الهند, تنسيق طبي في الهند",
    brand: "HealBay Connect",
    nav: [
      { label: "من نحن", href: "#about" },
      { label: "المستشفيات", href: "#hospitals" },
      { label: "التخصصات", href: "#specialties" },
      { label: "الخدمات", href: "#services" },
      { label: "الخطوات", href: "#process" },
      { label: "التواصل", href: "#inquiry" },
    ],
    hero: {
      eyebrow: "السياحة العلاجية في الهند",
      journeyHighlight: "تميز سريري • مستشفيات معتمدة • دعم متكامل للتعافي",
      journeySubHighlight:
        "الأورام، القلب، جراحات الأعصاب وغيرها مع تنسيق دولي موثوق",
      title: "تنسيق موثوق للعلاج الطبي في الهند",
      subtitle:
        "احصل على رعاية سريرية عالمية ومسارات تعافٍ مخصصة عبر أفضل مستشفيات كيرالا المعتمدة من JCI وNABH، بخبرة تتجاوز عقدًا في تنسيق المرضى الدوليين.",
      description:
        "تدعم HealBay Connect المرضى الدوليين الباحثين عن العلاج الطبي في الهند والسياحة العلاجية في الهند، مع تنسيق موثوق للمستشفيات في كيرالا وتخطيط علاجي شفاف بإشراف مختصين.",
      ctaWhatsapp: "تواصل عبر واتساب",
      ctaCall: "اتصل الآن",
      ctaEmail: "إرسال استفسار عبر البريد",
      badge: "خبرة مشتركة تقارب 10 سنوات في تنسيق الرعاية",
      logoIntroTitle: "الرعاية الموثوقة تبدأ من هنا",
      logoIntroSubtitle:
        "مرّر للأسفل لاستعراض تفاصيل تنسيق العلاج والمستشفيات الشريكة وخطط التعافي.",
      logoIntroButton: "عرض التفاصيل",
    },
    about: {
      title: "عن HealBay Connect",
      body: "HealBay Connect هي خدمة تنسيق علاج طبي مقرها كيرالا، الهند، لتسهيل وصول المرضى الدوليين للرعاية المناسبة. نركز على الثقة والشفافية والإرشاد الشخصي منذ التواصل الأول وحتى المتابعة بعد العلاج.",
      highlights: [
        "خبرة مشتركة تقارب 10 سنوات في تنسيق العلاج",
        "دعم المرضى الدوليين خلال رحلة العلاج في الهند",
        "تواصل شفاف وتنسيق أخلاقي",
        "دعم المرضى باللغتين الإنجليزية والعربية",
        "إرشاد شخصي طوال رحلة العلاج",
      ],
    },
    hospitals: {
      title: "شركاؤنا من المستشفيات الرائدة",
      intro:
        "مستشفياتنا الشريكة من أبرز المؤسسات متعددة التخصصات في كيرالا وتقدم رعاية متقدمة وخدمات للمرضى الدوليين.",
      cards: [
        {
          name: "Aster Medcity Kochi",
          accreditation: ["اعتماد JCI", "اعتماد NABH"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/m8ngnnhg_69387.jpg",
          description:
            "رعاية طبية متقدمة شاملة مع أقسام معترف بها دوليًا وخدمات مخصصة للمرضى الدوليين.",
        },
        {
          name: "Rajagiri Hospital",
          accreditation: ["اعتماد NABH", "شهادة ISO"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/kdr75giw_69388.jpg",
          description:
            "تشخيصات متقدمة ومسارات علاجية بقيادة مختصين مع تركيز على جودة النتائج.",
        },
        {
          name: "VPS Lakeshore Hospital",
          accreditation: ["اعتماد NABH", "شهادة ISO"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/tav6yx8v_69389.jpg",
          description:
            "خبرة قوية في تخصصات متعددة مع دعم فعّال للمرضى الدوليين.",
        },
        {
          name: "Adlux Apollo Hospital",
          accreditation: ["متوافق مع NABH", "بروتوكولات Apollo السريرية"],
          image:
            "https://customer-assets.emergentagent.com/job_med-connect-36/artifacts/1j3qctgi_unnamed.jpg",
          description:
            "مستشفى حديث متعدد التخصصات بمعايير Apollo، يقدّم تشخيصًا متقدمًا ودعمًا موثوقًا للمرضى الدوليين.",
        },
      ],
    },
    specialties: {
      title: "التخصصات الطبية",
      items: [
        {
          title: "جراحة العظام",
          description: "رعاية العظام والمفاصل والحركة عبر أطباء موثوقين.",
          icon: "Bone",
        },
        {
          title: "الأعصاب وجراحة المخ والأعصاب",
          description:
            "رعاية متخصصة لحالات الدماغ والعمود الفقري والجهاز العصبي.",
          icon: "Brain",
        },
        {
          title: "المسالك البولية",
          description: "تقييم وعلاج مخصص لحالات المسالك البولية.",
          icon: "UrologySpecialty",
        },
        {
          title: "أمراض القلب",
          description: "رعاية قلبية شاملة مع فحوصات وإجراءات متقدمة.",
          icon: "HeartPulse",
        },
        {
          title: "الجهاز الهضمي",
          description: "تنسيق علاج أمراض الجهاز الهضمي مع أفضل المختصين.",
          icon: "Stomach",
        },
        {
          title: "أمراض النساء",
          description: "مسارات رعاية صحية نسائية بدعم طبي إنساني.",
          icon: "GynecologySpecialty",
        },
        {
          title: "الأورام",
          description: "إرشاد منظم للاستشارات والعلاج والمتابعة لمرضى الأورام.",
          icon: "ShieldPlus",
        },
        {
          title: "طب الأطفال",
          description:
            "مسارات علاج متخصصة للأطفال مع تنسيق يراعي راحة الأسرة واحتياجات الطفل.",
          icon: "Baby",
        },
      ],
    },
    services: {
      title: "الخدمات المقدمة",
      items: [
        { title: "تقييم التقارير الطبية", icon: "FileSearch" },
        { title: "تنسيق خطة العلاج مع المستشفيات", icon: "ClipboardCheck" },
        { title: "تقدير تكاليف شفاف", icon: "BadgeIndianRupee" },
        { title: "حجز المواعيد مع الأطباء المختصين", icon: "CalendarCheck" },
        { title: "مساعدة التأشيرة", icon: "Globe" },
        { title: "إرشاد السفر والإقامة", icon: "Plane" },
        { title: "منسق مخصص لكل مريض", icon: "Users" },
        { title: "متابعة ما بعد العلاج", icon: "MessageSquareText" },
      ],
    },
    conciergeBanner: {
      headline: "الرعاية السريرية أولًا والتعافي المدعوم من كيرالا",
      subheadline:
        "احصل على مسارات علاج يقودها مختصون مع تخطيط تعافٍ مناسب للأسرة ودعم عافية من خلال شريك تنسيق طبي موثوق.",
      cta: "تحدث مع منسق السفر لدينا",
      image:
        "https://images.unsplash.com/photo-1629813538702-64c925934e19?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    process: {
      title: "كيف تتم الرحلة العلاجية",
      steps: [
        "تواصل مع HealBay Connect وشارك التقارير الطبية",
        "استلم مسارات علاج مخصصة وتكاليف شفافة من المستشفيات الشريكة خلال 48 ساعة",
        "خطط للسفر إلى كيرالا مع مواعيد مرتبة مسبقًا",
        "احصل على دعم أثناء العلاج وفترة التعافي",
      ],
    },
    why: {
      title: "لماذا تختار HealBay Connect",
      points: [
        "خبرة تقارب 10 سنوات في تنسيق الرعاية الصحية",
        "شبكة مستشفيات موثوقة في كيرالا",
        "تواصل شفاف ونهج أخلاقي",
        "دعم مخصص طوال الرحلة العلاجية",
        "مساعدة باللغتين العربية والإنجليزية",
        "تركيز قوي على راحة المريض ووضوح المعلومات",
      ],
    },
    testimonials: {
      title: "تقييمات Google من مرضى دوليين",
      subtitle:
        "تجارب موثوقة شاركتها العائلات التي حصلت على دعم تنسيق العلاج عبر HealBay Connect.",
      badgeLabel: "عرض على Google",
      badgeTitle: "افتح تقييمات HealBay Connect على Google",
      ratingSuffix: "استنادًا إلى {count} تقييمات على Google",
      verifiedLabel: "تقييم Google موثّق",
      shareTitle: "هل تلقيت العلاج معنا؟",
      shareButton: "شارك تجربتك",
    },
    inquiry: {
      title: "نموذج الاستفسار عبر البريد",
      subtitle:
        "شارك تفاصيل حالتك الطبية بأمان. فريقنا يدعم التواصل باللغتين العربية والإنجليزية مع مساعدة مخصصة للوثائق الطبية لأسر الخليج.",
      fields: {
        fullName: "الاسم الكامل",
        country: "الدولة",
        phone: "الهاتف / واتساب",
        email: "البريد الإلكتروني",
        treatmentType: "نوع العلاج",
        message: "ملخص الحالة / رسالة الاستفسار",
      },
      uploadLabel: "رفع التقارير الطبية",
      uploadHelp:
        "الصيغ المقبولة: PDF/JPG/PNG. أرفق أحدث التقارير لتسريع المراجعة السريرية.",
      securityNote:
        "🛡️ ملفاتك الطبية مشفرة وسرية بالكامل، ويتم مشاركتها حصريًا مع اللجان السريرية في المستشفيات المعتمدة لتقييم العلاج.",
      submit: "إرسال الاستفسار",
      success: "شكرًا لك. تم استلام استفسارك بنجاح.",
      error: "تعذر الإرسال الآن. يرجى التواصل عبر واتساب أو الاتصال المباشر.",
      validationError: "يرجى التحقق من بيانات النموذج والمحاولة مرة أخرى.",
    },
    cta: {
      title: "ابدأ رحلتك العلاجية في الهند مع HealBay Connect",
      description:
        "تواصل مع فريقنا للحصول على تنسيق علاجي واضح ودعم إنساني طوال الرحلة.",
      whatsapp: "استشارة واتساب",
      call: "اتصل بفريقنا",
      email: "استفسار عبر البريد",
    },
    footer: {
      location: "كيرالا، الهند",
      disclaimer:
        "تقدم HealBay Connect خدمات التنسيق والتسهيل الطبي فقط. العلاج الطبي يتم تقديمه بواسطة مستشفيات مرخصة وكوادر صحية مؤهلة.",
      privacy: "سياسة الخصوصية",
      adminAccess: "تسجيل دخول لوحة الإدارة",
      socialTitle: "تابعنا",
      instagram: "إنستغرام",
      facebook: "فيسبوك",
      rights: "© HealBay Connect. جميع الحقوق محفوظة.",
    },
    privacyPolicy: {
      title: "سياسة الخصوصية",
      intro:
        "تلتزم HealBay Connect بحماية جميع البيانات الشخصية والطبية التي تتم مشاركتها عبر هذا الموقع.",
      points: [
        "نجمع فقط البيانات الأساسية اللازمة لتنسيق العلاج، بما في ذلك بيانات التواصل والملخصات الطبية.",
        "جميع الملفات الطبية المرسلة تكون مشفرة وتخضع لبروتوكولات سرية صارمة.",
        "تتم مشاركة بيانات المريض فقط مع اللجان السريرية في المستشفيات الشريكة المعتمدة لغرض التقييم ووضع خطة العلاج.",
        "لا نقوم ببيع أو مشاركة بيانات المرضى مع أي طرف غير مصرح له.",
      ],
      contact: "لطلبات الخصوصية، تواصل مع: care@healbayconnect.com",
    },
    floating: "واتساب",
    languageButton: "English",
  },
};

const defaultForm = {
  full_name: "",
  country: "",
  phone_whatsapp: "",
  email: "",
  treatment_type: "",
  message: "",
};

function App() {
  const [lang, setLang] = useState("en");
  const [formData, setFormData] = useState(defaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStickyHeaderLogo, setShowStickyHeaderLogo] = useState(false);
  const [logoScrollScale, setLogoScrollScale] = useState(1);

  const isArabic = lang === "ar";
  const t = useMemo(() => CONTENT[lang], [lang]);
  const isAdminPortal =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminPortal) {
      return;
    }

    document.documentElement.lang = isArabic ? "ar" : "en";
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.title = t.metaTitle;

    const upsertMeta = (name, content) => {
      let tag = document.querySelector(`meta[name=\"${name}\"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    upsertMeta("description", t.metaDescription);
    upsertMeta("keywords", t.metaKeywords);
  }, [isArabic, t, isAdminPortal]);

  useEffect(() => {
    if (isAdminPortal) {
      return;
    }

    const handleScroll = () => {
      const currentY = window.scrollY;
      setShowStickyHeaderLogo(currentY > 220);

      const progress = Math.min(currentY / 420, 1);
      const nextScale = 1 - progress * 0.68;
      setLogoScrollScale(Number(nextScale.toFixed(3)));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAdminPortal]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] ?? null;
    setSelectedFile(selected);
  };

  const scrollToInquiry = () => {
    const inquiryElement = document.getElementById("inquiry");
    inquiryElement?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitInquiry = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        language: lang,
        has_medical_reports: Boolean(selectedFile),
        medical_reports_filename: selectedFile?.name ?? "",
      });

      toast.success(t.inquiry.success);
      setFormData(defaultForm);
      setSelectedFile(null);
    } catch (error) {
      console.error(error);
      const statusCode = error?.response?.status;
      if (statusCode === 422) {
        toast.error(t.inquiry.validationError);
      } else {
        toast.error(t.inquiry.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAdminPortal) {
    return <AdminDashboard />;
  }

  return (
    <div
      className={`min-h-screen bg-white text-slate-900 ${isArabic ? "font-arabic" : "font-body"}`}
      data-testid="healbay-root-page"
    >
      <Toaster position={isArabic ? "top-left" : "top-right"} richColors />

      <header
        className="fixed left-0 right-0 top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md"
        data-testid="top-navigation"
      >
        <div className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-8">
          <div
            className="hidden items-center gap-2 lg:flex"
            data-testid="header-left-social-group"
          >
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-left-instagram-link"
              aria-label="Instagram"
            >
              <Icons.Instagram className="h-3.5 w-3.5" aria-hidden="true" />
            </a>

            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-left-facebook-link"
              aria-label="Facebook"
            >
              <Icons.Facebook className="h-3.5 w-3.5" aria-hidden="true" />
            </a>

            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-left-whatsapp-link"
              aria-label="WhatsApp"
            >
              <WhatsAppBrandIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>

          <div
            className="flex items-center gap-2 md:hidden"
            data-testid="header-mobile-social-group"
          >
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-mobile-instagram-link"
              aria-label="Instagram"
            >
              <Icons.Instagram className="h-3.5 w-3.5" aria-hidden="true" />
            </a>

            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-mobile-facebook-link"
              aria-label="Facebook"
            >
              <Icons.Facebook className="h-3.5 w-3.5" aria-hidden="true" />
            </a>

            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-700"
              data-testid="header-mobile-whatsapp-link"
              aria-label="WhatsApp"
            >
              <WhatsAppBrandIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>

          <a
            href="#logo-intro-section"
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 origin-center transition-all duration-700 ease-out ${
              showStickyHeaderLogo
                ? "scale-100 opacity-100"
                : "pointer-events-none scale-90 opacity-0"
            }`}
            data-testid="navbar-centered-sticky-logo-link"
          >
            <img
              src={LOGO_NAVBAR_URL}
              alt="HealBay Connect logo"
              className="h-14 w-auto max-w-[240px] rounded-md object-contain object-center md:h-16 md:max-w-[300px]"
              data-testid="navbar-logo-image"
            />
          </a>

          <div className="flex items-center gap-3" data-testid="header-actions">
            <Button
              type="button"
              onClick={() => setLang((prev) => (prev === "en" ? "ar" : "en"))}
              className="rounded-full bg-white px-4 text-slate-700 shadow-sm ring-1 ring-slate-200 transition-colors duration-300 hover:bg-slate-50"
              data-testid="language-toggle-button"
            >
              <Icons.Globe className="h-4 w-4" aria-hidden="true" />
              {t.languageButton}
            </Button>

            <a
              href={CONTACT.phoneLink}
              className="hidden rounded-full bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-cyan-700 md:inline-flex"
              data-testid="header-call-link"
            >
              {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </header>

      <aside
        className="fixed left-4 top-[120px] z-40 hidden rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-[0_10px_22px_rgba(15,23,42,0.08)] backdrop-blur xl:block"
        data-testid="separate-left-nav-panel"
      >
        <nav
          className="flex flex-col items-start gap-1.5"
          data-testid="desktop-navigation-links"
        >
          {t.nav.map((navItem) => (
            <a
              key={navItem.href}
              href={navItem.href}
              className="rounded-md px-2 py-1 text-sm font-medium text-slate-600 transition-colors duration-300 hover:bg-cyan-50 hover:text-cyan-700"
              data-testid={`nav-link-${navItem.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {navItem.label}
            </a>
          ))}
        </nav>
      </aside>

      <main
        className="pt-[130px] xl:ml-[150px]"
        data-testid="main-content-wrapper"
      >
        <section
          id="logo-intro-section"
          className="border-b border-slate-100 bg-gradient-to-b from-cyan-50/60 to-white py-8 md:py-10"
          data-testid="logo-intro-section"
        >
          <div
            className="mx-auto flex min-h-[42vh] max-w-7xl flex-col items-center justify-start gap-4 px-6 pt-4 text-center md:min-h-[46vh] md:px-8 md:pt-6"
            data-testid="logo-intro-content"
          >
            <img
              src={LOGO_NAVBAR_URL}
              alt="HealBay Connect logo"
              className="h-48 w-auto max-w-[620px] object-contain transition-transform duration-500 ease-out md:h-64 md:max-w-[820px]"
              style={{
                transform: `scale(${logoScrollScale})`,
                transformOrigin: "top center",
              }}
              data-testid="logo-intro-image"
            />
          </div>
        </section>

        <section
          className="border-b border-slate-100 bg-white py-5 md:py-7"
          data-testid="travel-concierge-banner-section"
        >
          <div className="mx-auto flex max-w-7xl px-6 md:px-8">
            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="relative w-full overflow-hidden rounded-2xl border border-cyan-100 shadow-[0_10px_26px_rgba(8,145,178,0.16)] transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="travel-concierge-banner-card"
            >
              <img
                src={t.conciergeBanner.image}
                alt="Kerala natural wellness retreat landscape"
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="lazy"
                data-testid="travel-concierge-banner-image"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/92 via-slate-900/78 to-slate-900/62" />

              <div
                className="relative space-y-3 px-4 py-4 md:px-5 md:py-5"
                data-testid="travel-concierge-banner-content"
              >
                <span
                  className="inline-flex rounded-full border border-white/40 bg-white/10 px-3 py-1 text-[11px] font-semibold tracking-wide text-white"
                  data-testid="travel-concierge-banner-tag"
                >
                  Premium Medical Tourism Experience
                </span>

                <h3
                  className="font-heading text-lg font-extrabold leading-tight text-white md:text-xl"
                  data-testid="travel-concierge-banner-headline"
                >
                  {t.conciergeBanner.headline}
                </h3>

                <div className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white">
                  <WhatsAppBrandIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  <span data-testid="travel-concierge-whatsapp-button">
                    {t.conciergeBanner.cta}
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>

        <section
          id="hero"
          className="relative isolate overflow-hidden border-b border-slate-100"
          data-testid="hero-section"
        >
          <img
            src="https://images.unsplash.com/photo-1759270463226-c5e04a4542c1?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Abstract medical care background"
            className="absolute inset-0 h-full w-full object-cover object-center"
            data-testid="hero-background-image"
          />
          <div className="absolute inset-0 bg-white/85" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(8,145,178,0.18),rgba(255,255,255,0.05)_55%)]" />

          <div className="relative mx-auto flex max-w-7xl flex-col gap-8 px-6 py-20 md:px-8 md:py-28 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-6" data-testid="hero-copy-block">
              <span
                className="inline-flex rounded-full border border-cyan-100 bg-cyan-50 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-700"
                data-testid="hero-eyebrow-text"
              >
                {t.hero.eyebrow}
              </span>

              <div
                className="inline-flex w-fit flex-wrap items-center gap-2 rounded-full border border-teal-200 bg-teal-50/90 px-4 py-2 text-xs font-semibold text-teal-800"
                data-testid="hero-journey-highlight"
              >
                <Icons.Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{t.hero.journeyHighlight}</span>
              </div>

              <p
                className="text-sm font-medium text-slate-700"
                data-testid="hero-journey-sub-highlight"
              >
                {t.hero.journeySubHighlight}
              </p>

              <h1
                className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
                data-testid="hero-main-heading"
              >
                <span data-testid="hero-company-name">{t.brand} — </span>
                <span>{t.hero.title}</span>
              </h1>

              <p
                className="text-base text-slate-700 md:text-lg"
                data-testid="hero-subheading-text"
              >
                {t.hero.subtitle}
              </p>

              <p
                className="max-w-3xl text-sm leading-relaxed text-slate-600 md:text-base"
                data-testid="hero-description-text"
              >
                {t.hero.description}
              </p>

              <div
                className="flex flex-wrap gap-3"
                data-testid="hero-cta-group"
              >
                <Button
                  asChild
                  className="rounded-full bg-[#25D366] px-6 py-5 text-white transition-colors duration-300 hover:bg-[#20bd5a]"
                  data-testid="hero-whatsapp-button"
                >
                  <a
                    href={CONTACT.whatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-whatsapp-link"
                  >
                    <WhatsAppBrandIcon className="h-4 w-4" aria-hidden="true" />
                    {t.hero.ctaWhatsapp}
                  </a>
                </Button>

                <Button
                  asChild
                  className="rounded-full bg-cyan-600 px-6 py-5 text-white transition-colors duration-300 hover:bg-cyan-700"
                  data-testid="hero-call-button"
                >
                  <a href={CONTACT.phoneLink} data-testid="hero-call-link">
                    <Icons.Phone className="h-4 w-4" aria-hidden="true" />
                    {t.hero.ctaCall}
                  </a>
                </Button>

                <Button
                  type="button"
                  onClick={scrollToInquiry}
                  className="rounded-full bg-white px-6 py-5 text-slate-900 ring-1 ring-slate-300 transition-colors duration-300 hover:bg-slate-50"
                  data-testid="hero-email-inquiry-button"
                >
                  <Icons.Mail className="h-4 w-4" aria-hidden="true" />
                  {t.hero.ctaEmail}
                </Button>
              </div>

              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                title={t.testimonials.badgeTitle}
                className="inline-flex w-fit items-center gap-3 rounded-full border border-slate-200 bg-white/90 py-2 pl-2 pr-4 shadow-sm backdrop-blur transition-colors duration-300 hover:border-cyan-300 hover:bg-cyan-50/80"
                data-testid="hero-trust-strip"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
                  <GoogleGIcon className="h-4 w-4" />
                </span>
                <span
                  className="text-sm font-bold text-slate-900"
                  data-testid="hero-trust-rating"
                >
                  {GOOGLE_RATING.value}
                </span>
                <span
                  className="flex items-center gap-0.5"
                  data-testid="hero-trust-stars"
                >
                  {Array.from({ length: 5 }).map((_, starIndex) => (
                    <Icons.Star
                      key={`hero-star-${starIndex}`}
                      className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                      aria-hidden="true"
                    />
                  ))}
                </span>
                <span
                  className="text-xs font-medium text-slate-600"
                  data-testid="hero-trust-count"
                >
                  {t.testimonials.ratingSuffix.replace(
                    "{count}",
                    GOOGLE_RATING.count,
                  )}
                </span>
              </a>
            </div>

            <Card
              className="border border-slate-200/80 bg-white/90 p-0 shadow-[0_12px_30px_rgba(15,23,42,0.10)]"
              data-testid="hero-highlight-card"
            >
              <CardContent className="space-y-4 p-7">
                <div
                  className="flex items-start gap-3"
                  data-testid="hero-highlight-row"
                >
                  <Icons.CheckCircle2
                    className="mt-1 h-5 w-5 text-cyan-600"
                    aria-hidden="true"
                  />
                  <p
                    className="text-sm font-medium text-slate-700"
                    data-testid="hero-experience-badge"
                  >
                    {t.hero.badge}
                  </p>
                </div>

                <div
                  className="grid grid-cols-1 gap-3 text-sm text-slate-600"
                  data-testid="hero-contact-grid"
                >
                  <a
                    href={CONTACT.phoneLink}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 transition-colors duration-300 hover:border-cyan-200 hover:bg-cyan-50/60"
                    data-testid="hero-contact-phone"
                  >
                    <Icons.Phone
                      className="h-4 w-4 text-cyan-700"
                      aria-hidden="true"
                    />
                    <span>{CONTACT.phoneDisplay}</span>
                  </a>

                  <a
                    href={CONTACT.emailLink}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 transition-colors duration-300 hover:border-cyan-200 hover:bg-cyan-50/60"
                    data-testid="hero-contact-email"
                  >
                    <Icons.Mail
                      className="h-4 w-4 text-cyan-700"
                      aria-hidden="true"
                    />
                    <span>{CONTACT.email}</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section
          id="about"
          className="border-b border-slate-100 py-16 md:py-24"
          data-testid="about-section"
        >
          <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-8 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="space-y-6" data-testid="about-content-column">
              <h2
                className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
                data-testid="about-heading"
              >
                {t.about.title}
              </h2>
              <p
                className="text-base leading-relaxed text-slate-600 md:text-lg"
                data-testid="about-body-text"
              >
                {t.about.body}
              </p>
            </div>

            <Card
              className="border-slate-200 bg-slate-50"
              data-testid="about-highlights-card"
            >
              <CardContent className="space-y-4 p-7">
                {t.about.highlights.map((highlight, index) => (
                  <div
                    key={highlight}
                    className="flex items-start gap-3"
                    data-testid={`about-highlight-${index + 1}`}
                  >
                    <Icons.CheckCircle2
                      className="mt-0.5 h-5 w-5 text-cyan-600"
                      aria-hidden="true"
                    />
                    <p
                      className="text-sm text-slate-700 md:text-base"
                      data-testid={`about-highlight-text-${index + 1}`}
                    >
                      {highlight}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        <section
          id="hospitals"
          className="border-b border-slate-100 py-16 md:py-24"
          data-testid="hospitals-section"
        >
          <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-8">
            <h2
              className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
              data-testid="hospitals-heading"
            >
              {t.hospitals.title}
            </h2>
            <p
              className="max-w-3xl text-base text-slate-600 md:text-lg"
              data-testid="hospitals-intro-text"
            >
              {t.hospitals.intro}
            </p>

            <div
              className="flex flex-wrap gap-2"
              data-testid="hospitals-accreditation-badges-row"
            >
              {["JCI Accredited", "NABH Accredited", "ISO Certified"].map(
                (badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-800"
                    data-testid={`accreditation-badge-${badge.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icons.BadgeCheck
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    {badge}
                  </span>
                ),
              )}
            </div>

            <div
              className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
              data-testid="hospitals-grid"
            >
              {t.hospitals.cards.map((hospital, index) => (
                <Card
                  key={hospital.name}
                  className="overflow-hidden border border-slate-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`hospital-card-${index + 1}`}
                >
                  <div
                    className="aspect-[16/10] overflow-hidden"
                    data-testid={`hospital-image-wrapper-${index + 1}`}
                  >
                    <img
                      src={hospital.image}
                      alt={hospital.name}
                      loading="lazy"
                      className={`h-full w-full object-cover transition-transform duration-500 ${
                        hospital.name === "Adlux Apollo Hospital"
                          ? "object-top scale-110"
                          : "object-center"
                      }`}
                      data-testid={`hospital-image-${index + 1}`}
                    />
                  </div>
                  <CardContent className="space-y-2 p-5">
                    <h3
                      className="font-heading text-xl font-semibold text-slate-900"
                      data-testid={`hospital-name-${index + 1}`}
                    >
                      {hospital.name}
                    </h3>

                    <div
                      className="flex flex-wrap gap-1.5"
                      data-testid={`hospital-accreditations-${index + 1}`}
                    >
                      {hospital.accreditation?.map((accreditation) => (
                        <span
                          key={`${hospital.name}-${accreditation}`}
                          className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700"
                          data-testid={`hospital-accreditation-chip-${hospital.name.replace(/\s+/g, "-").toLowerCase()}-${accreditation
                            .replace(/\s+/g, "-")
                            .toLowerCase()}`}
                        >
                          {accreditation}
                        </span>
                      ))}
                    </div>

                    <p
                      className="text-sm text-slate-600"
                      data-testid={`hospital-description-${index + 1}`}
                    >
                      {hospital.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section
          id="specialties"
          className="border-b border-slate-100 bg-slate-50/60 py-16 md:py-24"
          data-testid="specialties-section"
        >
          <div className="mx-auto max-w-6xl space-y-8 px-6 md:px-8">
            <h2
              className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
              data-testid="specialties-heading"
            >
              {t.specialties.title}
            </h2>

            <div
              className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
              data-testid="specialties-grid"
            >
              {t.specialties.items.map((item, index) => {
                const IconComponent = getIcon(item.icon);
                return (
                  <Card
                    key={item.title}
                    className="border border-slate-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                    data-testid={`specialty-card-${index + 1}`}
                  >
                    <CardContent className="space-y-3 p-4">
                      <div
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50"
                        data-testid={`specialty-icon-wrap-${index + 1}`}
                      >
                        <IconComponent
                          className="h-5 w-5 text-cyan-700"
                          aria-hidden="true"
                        />
                      </div>
                      <h3
                        className="font-heading text-xl font-semibold text-slate-900"
                        data-testid={`specialty-title-${index + 1}`}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="text-sm text-slate-600"
                        data-testid={`specialty-description-${index + 1}`}
                      >
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="services"
          className="border-b border-slate-100 py-16 md:py-24"
          data-testid="services-section"
        >
          <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-8">
            <h2
              className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
              data-testid="services-heading"
            >
              {t.services.title}
            </h2>
            <div
              className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
              data-testid="services-grid"
            >
              {t.services.items.map((service, index) => {
                const IconComponent = getIcon(service.icon);
                return (
                  <div
                    key={service.title}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-md"
                    data-testid={`service-card-${index + 1}`}
                  >
                    <IconComponent
                      className="mb-3 h-5 w-5 text-cyan-700"
                      aria-hidden="true"
                    />
                    <h3
                      className="font-heading text-base font-semibold text-slate-900"
                      data-testid={`service-title-${index + 1}`}
                    >
                      {service.title}
                    </h3>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="process"
          className="border-b border-slate-100 bg-slate-50/60 py-10 md:py-14"
          data-testid="process-section"
        >
          <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-8">
            <h2
              className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
              data-testid="process-heading"
            >
              {t.process.title}
            </h2>

            <div
              className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
              data-testid="process-grid"
            >
              {t.process.steps.map((step, index) => (
                <div
                  key={step}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                  data-testid={`process-step-card-${index + 1}`}
                >
                  <div
                    className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-cyan-600 text-white"
                    data-testid={`process-step-number-${index + 1}`}
                  >
                    {index + 1}
                  </div>
                  <p
                    className="text-sm leading-relaxed text-slate-700"
                    data-testid={`process-step-text-${index + 1}`}
                  >
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="border-b border-slate-100 py-16 md:py-24"
          data-testid="why-choose-section"
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-8 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div className="space-y-5" data-testid="why-choose-copy-column">
              <h2
                className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
                data-testid="why-choose-heading"
              >
                {t.why.title}
              </h2>
              <div
                className="space-y-3"
                data-testid="why-choose-points-wrapper"
              >
                {t.why.points.map((point, index) => (
                  <div
                    key={point}
                    className="flex items-start gap-3"
                    data-testid={`why-choose-point-${index + 1}`}
                  >
                    <Icons.CheckCircle2
                      className="mt-0.5 h-5 w-5 text-cyan-600"
                      aria-hidden="true"
                    />
                    <p
                      className="text-sm text-slate-700 md:text-base"
                      data-testid={`why-choose-point-text-${index + 1}`}
                    >
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="overflow-hidden rounded-3xl border border-slate-200"
              data-testid="why-choose-image-wrapper"
            >
              <img
                src="https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Doctor consultation with patient"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover object-center"
                data-testid="why-choose-image"
              />
            </div>
          </div>
        </section>

        <section
          className="border-b border-slate-100 bg-slate-50/60 py-16 md:py-24"
          data-testid="testimonials-section"
        >
          <div className="mx-auto max-w-7xl space-y-8 px-6 md:px-8">
            <div
              className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between"
              data-testid="testimonials-header"
            >
              <div className="space-y-4">
                <h2
                  className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
                  data-testid="testimonials-heading"
                >
                  {t.testimonials.title}
                </h2>
                <p
                  className="max-w-3xl text-base text-slate-600"
                  data-testid="testimonials-subtitle"
                >
                  {t.testimonials.subtitle}
                </p>
              </div>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                title={t.testimonials.badgeTitle}
                className="inline-flex w-fit items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-md"
                data-testid="google-rating-badge"
              >
                <GoogleGIcon className="h-9 w-9" />
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-heading text-2xl font-bold leading-none text-slate-900"
                      data-testid="google-rating-value"
                    >
                      {GOOGLE_RATING.value}
                    </span>
                    <div
                      className="flex items-center gap-0.5"
                      data-testid="google-rating-stars"
                    >
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <Icons.Star
                          key={`rating-star-${starIndex}`}
                          className="h-4 w-4 fill-amber-400 text-amber-400"
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                  </div>
                  <p
                    className="text-xs font-medium text-slate-500"
                    data-testid="google-rating-count"
                  >
                    {t.testimonials.ratingSuffix.replace(
                      "{count}",
                      GOOGLE_RATING.count,
                    )}
                  </p>
                </div>
              </a>
            </div>

            <ReviewCarousel t={t} />

            <div
              className="flex flex-col items-center gap-3 pt-2 text-center"
              data-testid="share-experience-block"
            >
              <p
                className="text-sm font-medium text-slate-600"
                data-testid="share-experience-title"
              >
                {t.testimonials.shareTitle}
              </p>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-slate-300 bg-white px-6 text-slate-800 hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-900"
              >
                <a
                  href={GOOGLE_WRITE_REVIEW_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-experience-button"
                >
                  <GoogleGIcon className="h-4 w-4" />
                  {t.testimonials.shareButton}
                  <Icons.ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section
          id="inquiry"
          className="border-b border-slate-100 py-16 md:py-24"
          data-testid="inquiry-section"
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-5" data-testid="inquiry-intro-column">
              <h2
                className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
                data-testid="inquiry-heading"
              >
                {t.inquiry.title}
              </h2>
              <p
                className="text-base text-slate-600"
                data-testid="inquiry-subtitle"
              >
                {t.inquiry.subtitle}
              </p>

              <div
                className="space-y-3"
                data-testid="inquiry-direct-contact-group"
              >
                <a
                  href={CONTACT.whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-full items-center justify-between rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800 transition-colors duration-300 hover:bg-green-100"
                  data-testid="inquiry-whatsapp-link"
                >
                  <span>{t.hero.ctaWhatsapp}</span>
                  <Icons.ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>

                <a
                  href={CONTACT.phoneLink}
                  className="inline-flex w-full items-center justify-between rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-medium text-cyan-800 transition-colors duration-300 hover:bg-cyan-100"
                  data-testid="inquiry-call-link"
                >
                  <span>{t.hero.ctaCall}</span>
                  <Icons.ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>

                <a
                  href={CONTACT.emailLink}
                  className="inline-flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-800 transition-colors duration-300 hover:bg-slate-50"
                  data-testid="inquiry-email-link"
                >
                  <span>{CONTACT.email}</span>
                  <Icons.ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            <Card
              className="border border-slate-200 bg-white"
              data-testid="inquiry-form-card"
            >
              <CardContent className="p-6 md:p-7">
                <form
                  className="space-y-4"
                  onSubmit={submitInquiry}
                  data-testid="inquiry-form"
                >
                  <div
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                    data-testid="inquiry-form-grid"
                  >
                    <div
                      className="space-y-2"
                      data-testid="full-name-field-wrap"
                    >
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor="full_name"
                        data-testid="full-name-label"
                      >
                        {t.inquiry.fields.fullName}
                      </label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleFormChange}
                        minLength={2}
                        required
                        data-testid="full-name-input"
                      />
                    </div>

                    <div className="space-y-2" data-testid="country-field-wrap">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor="country"
                        data-testid="country-label"
                      >
                        {t.inquiry.fields.country}
                      </label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleFormChange}
                        minLength={2}
                        required
                        data-testid="country-input"
                      />
                    </div>

                    <div className="space-y-2" data-testid="phone-field-wrap">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor="phone_whatsapp"
                        data-testid="phone-label"
                      >
                        {t.inquiry.fields.phone}
                      </label>
                      <Input
                        id="phone_whatsapp"
                        type="tel"
                        name="phone_whatsapp"
                        value={formData.phone_whatsapp}
                        onChange={handleFormChange}
                        minLength={7}
                        required
                        data-testid="phone-input"
                      />
                    </div>

                    <div className="space-y-2" data-testid="email-field-wrap">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor="email"
                        data-testid="email-label"
                      >
                        {t.inquiry.fields.email}
                      </label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div
                    className="space-y-2"
                    data-testid="treatment-type-field-wrap"
                  >
                    <label
                      className="text-sm font-medium text-slate-700"
                      htmlFor="treatment_type"
                      data-testid="treatment-type-label"
                    >
                      {t.inquiry.fields.treatmentType}
                    </label>
                    <Input
                      id="treatment_type"
                      name="treatment_type"
                      value={formData.treatment_type}
                      onChange={handleFormChange}
                      minLength={2}
                      required
                      data-testid="treatment-type-input"
                    />
                  </div>

                  <div className="space-y-2" data-testid="message-field-wrap">
                    <label
                      className="text-sm font-medium text-slate-700"
                      htmlFor="message"
                      data-testid="message-label"
                    >
                      {t.inquiry.fields.message}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows={5}
                      minLength={10}
                      required
                      data-testid="message-input"
                    />
                  </div>

                  <div
                    className="space-y-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4"
                    data-testid="upload-placeholder-box"
                  >
                    <p
                      className="text-sm font-medium text-slate-700"
                      data-testid="upload-placeholder-label"
                    >
                      <Icons.UploadCloud
                        className="mr-2 inline h-4 w-4 text-cyan-700"
                        aria-hidden="true"
                      />
                      {t.inquiry.uploadLabel}
                    </p>

                    <Input
                      type="file"
                      onChange={handleFileChange}
                      data-testid="medical-report-file-input"
                      className="bg-white"
                    />

                    <p
                      className="text-xs text-slate-500"
                      data-testid="upload-placeholder-help-text"
                    >
                      {selectedFile ? selectedFile.name : t.inquiry.uploadHelp}
                    </p>

                    <p
                      className="text-xs font-medium text-emerald-700"
                      data-testid="upload-security-note-text"
                    >
                      {t.inquiry.securityNote}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-cyan-600 py-6 text-white transition-colors duration-300 hover:bg-cyan-700"
                    data-testid="inquiry-submit-button"
                  >
                    {isSubmitting ? "..." : t.inquiry.submit}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 md:py-24" data-testid="final-cta-section">
          <div className="mx-auto max-w-7xl px-6 md:px-8">
            <Card
              className="border border-cyan-200 bg-cyan-50/60"
              data-testid="final-cta-card"
            >
              <CardContent className="space-y-6 p-8 text-center md:p-12">
                <h2
                  className="font-heading text-3xl font-bold text-slate-900 md:text-4xl"
                  data-testid="final-cta-heading"
                >
                  {t.cta.title}
                </h2>
                <p
                  className="mx-auto max-w-3xl text-base text-slate-700"
                  data-testid="final-cta-description"
                >
                  {t.cta.description}
                </p>

                <div
                  className="flex flex-wrap items-center justify-center gap-3"
                  data-testid="final-cta-button-group"
                >
                  <Button
                    asChild
                    className="rounded-full bg-[#25D366] px-6 py-5 text-white transition-colors duration-300 hover:bg-[#20bd5a]"
                    data-testid="final-cta-whatsapp-button"
                  >
                    <a
                      href={CONTACT.whatsappLink}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="final-cta-whatsapp-link"
                    >
                      {t.cta.whatsapp}
                    </a>
                  </Button>

                  <Button
                    asChild
                    className="rounded-full bg-cyan-600 px-6 py-5 text-white transition-colors duration-300 hover:bg-cyan-700"
                    data-testid="final-cta-call-button"
                  >
                    <a
                      href={CONTACT.phoneLink}
                      data-testid="final-cta-call-link"
                    >
                      {t.cta.call}
                    </a>
                  </Button>

                  <Button
                    asChild
                    className="rounded-full bg-white px-6 py-5 text-slate-900 ring-1 ring-slate-300 transition-colors duration-300 hover:bg-slate-50"
                    data-testid="final-cta-email-button"
                  >
                    <a href="#inquiry" data-testid="final-cta-email-link">
                      {t.cta.email}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <footer className="bg-slate-900 py-10" data-testid="site-footer">
        <div className="mx-auto max-w-7xl space-y-4 px-6 text-slate-200 md:px-8">
          <div
            className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-3 py-2 shadow-[0_10px_22px_rgba(2,6,23,0.25)]"
            data-testid="footer-brand-wrap"
          >
            <img
              src={LOGO_FOOTER_URL}
              alt="HealBay Connect logo"
              className="h-14 w-auto max-w-[160px] rounded-lg object-contain object-left drop-shadow-[0_4px_12px_rgba(255,255,255,0.2)] md:h-16 md:max-w-[190px]"
              data-testid="footer-logo-image"
            />
            <h3
              className="hidden font-heading text-2xl font-semibold md:block"
              data-testid="footer-brand-name"
            >
              {t.brand}
            </h3>
          </div>
          <p
            className="text-sm text-slate-300"
            data-testid="footer-location-text"
          >
            {t.footer.location}
          </p>
          <p className="text-sm text-slate-300" data-testid="footer-phone-text">
            Phone / WhatsApp: {CONTACT.phoneDisplay}
          </p>
          <p className="text-sm text-slate-300" data-testid="footer-email-text">
            Email: {CONTACT.email}
          </p>
          <p
            className="max-w-4xl text-xs leading-relaxed text-slate-400"
            data-testid="footer-disclaimer-text"
          >
            {t.footer.disclaimer}
          </p>
          <div
            className="flex flex-col items-start gap-2"
            data-testid="footer-link-group"
          >
            <a
              href="#"
              className="inline-flex text-sm text-cyan-300 transition-colors duration-300 hover:text-cyan-200"
              data-testid="footer-privacy-link"
            >
              {t.footer.privacy}
            </a>
            <a
              href="/admin"
              className="inline-flex text-sm text-cyan-300 transition-colors duration-300 hover:text-cyan-200"
              data-testid="footer-admin-dashboard-link"
            >
              {t.footer.adminAccess}
            </a>
          </div>

          <div className="space-y-2" data-testid="footer-social-links-group">
            <p
              className="text-sm font-semibold text-slate-200"
              data-testid="footer-social-title"
            >
              {t.footer.socialTitle}
            </p>
            <div
              className="flex flex-wrap gap-2"
              data-testid="footer-social-links-wrap"
            >
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-200"
                data-testid="footer-instagram-link"
              >
                <Icons.Instagram className="h-3.5 w-3.5" aria-hidden="true" />
                {t.footer.instagram}
              </a>

              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors duration-300 hover:border-cyan-300 hover:text-cyan-200"
                data-testid="footer-facebook-link"
              >
                <Icons.Facebook className="h-3.5 w-3.5" aria-hidden="true" />
                {t.footer.facebook}
              </a>
            </div>
          </div>

          <p
            className="text-xs text-slate-500"
            data-testid="footer-copyright-text"
          >
            {t.footer.rights}
          </p>
        </div>
      </footer>

      <a
        href={CONTACT.whatsappLink}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-4 right-4 z-50 inline-flex h-10 w-[178px] items-center justify-center gap-2 rounded-full bg-[#25D366] px-3 py-0 text-[12px] font-semibold text-white shadow-[0_10px_20px_rgba(37,211,102,0.4)] transition-transform duration-300 hover:-translate-y-1"
        data-testid="floating-whatsapp-button"
      >
        <WhatsAppBrandIcon className="h-5 w-5" aria-hidden="true" />
        <span data-testid="floating-whatsapp-text">{t.floating}</span>
      </a>
    </div>
  );
}

export default App;
